import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { validators } from '../../utils/validators';
import { getLatestReportByEmail } from '../../services/diagnosisReports.service';
import Swal from 'sweetalert2';

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, register, logout } = useAuth();
    
    // Determine initial mode based on URL or state
    const [isSignUp, setIsSignUp] = useState(false);
    
    useEffect(() => {
        if (location.pathname === '/register') {
            setIsSignUp(true);
        } else {
            setIsSignUp(false);
        }
    }, [location.pathname]);

    // Form states
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
    
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (location.state?.success) {
            setSuccessMessage(location.state.success);
        }
    }, [location.state]);

    const handleLoginChange = (e) => {
        setLoginData({ ...loginData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
    };

    const handleRegisterChange = (e) => {
        setRegisterData({ ...registerData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setSuccessMessage('');

        const validation = validators.validateForm(loginData, {
            email: [{ type: 'required', message: 'Email is required' }, { type: 'email', message: 'Invalid email' }],
            password: [{ type: 'required', message: 'Password is required' }]
        });

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        setLoading(true);
        const result = await login(loginData.email, loginData.password);

        if (result.success) {
            if (result.data.role === 'admin') {
                setMessage('Please use the Admin Portal to sign in.');
                logout();
            } else {
                await Swal.fire({
                    icon: 'success',
                    title: 'Welcome Back!',
                    text: `Hello ${result.data.name}, you have logged in successfully.`,
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#ffffff',
                    iconColor: '#069668'
                });
                
                try {
                    const pendingData = localStorage.getItem('pendingDiagnosisData');
                    if (pendingData) {
                        navigate('/begin-with-clarity');
                    } else {
                        const latestReport = await getLatestReportByEmail(result.data.email, result.data.id || result.data.uid);
                        if (latestReport) {
                            navigate(`/diagnosis-report/${latestReport.id || latestReport.reportId}`);
                        } else {
                            navigate('/begin-with-clarity');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching latest report during login:', error);
                    navigate('/begin-with-clarity');
                }
            }
        } else {
            await Swal.fire({
                icon: 'error',
                title: 'Login Failed',
                text: result.error || 'Invalid email or password. Please try again.',
                confirmButtonColor: '#ef4444'
            });
            setMessage(result.error || 'Invalid credentials.');
        }
        setLoading(false);
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setSuccessMessage('');

        if (registerData.password !== registerData.confirmPassword) {
            setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
            await Swal.fire({
                icon: 'warning',
                title: 'Check Form',
                text: 'Passwords do not match. Please verify and try again.',
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        const validation = validators.validateForm(registerData, {
            name: [{ type: 'required', message: 'Name is required' }],
            email: [{ type: 'required', message: 'Email is required' }, { type: 'email', message: 'Invalid email' }],
            password: [{ type: 'required', message: 'Password is required' }, { type: 'minLength', value: 6, message: 'Min 6 chars' }]
        });

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        setLoading(true);
        const result = await register({
            email: registerData.email,
            password: registerData.password,
            name: registerData.name
        });

        if (result.success) {
            await logout(); // Reset state
            await Swal.fire({
                icon: 'success',
                title: 'Account Created!',
                text: 'Your registration was successful. Please login to continue.',
                confirmButtonColor: '#069668'
            });
            setIsSignUp(false);
            navigate('/login');
        } else {
            await Swal.fire({
                icon: 'error',
                title: 'Registration Error',
                text: result.error || 'This email might already be in use.',
                confirmButtonColor: '#ef4444'
            });
            setMessage(result.error || 'Registration failed.');
        }
        setLoading(false);
    };

    const toggleMode = () => {
        setIsSignUp(!isSignUp);
        setMessage('');
        setErrors({});
        setSuccessMessage('');
        // Update URL without full refresh
        if (!isSignUp) {
            window.history.pushState(null, '', '/register');
        } else {
            window.history.pushState(null, '', '/login');
        }
    };

    return (
        <div className="auth-page">
            <Link to="/" className="back-to-website-btn">
                <span>←</span> Back to Website
            </Link>
            <div className={`auth-wrapper ${isSignUp ? 'right-panel-active' : ''}`}>
                
                {/* Sign Up Form */}
                <div className="form-container sign-up-container">
                    <form className="auth-form-content" onSubmit={handleRegisterSubmit}>
                        <img src="/Yathartha-Logo.png" alt="Yathartha" style={{ height: '40px', marginBottom: '10px' }} />
                        <h2>Create Account</h2>
                        <div style={{ width: '100%' }}>
                            <input 
                                type="text" 
                                name="name" 
                                placeholder="Full Name" 
                                className="auth-input-field" 
                                onChange={handleRegisterChange}
                                value={registerData.name}
                                required
                            />
                            {errors.name && <span className="error-text" style={{ fontSize: '11px', color: 'red' }}>{errors.name}</span>}
                            
                            <input 
                                type="email" 
                                name="email" 
                                placeholder="Email" 
                                className="auth-input-field" 
                                onChange={handleRegisterChange}
                                value={registerData.email}
                                required
                            />
                            {errors.email && <span className="error-text" style={{ fontSize: '11px', color: 'red' }}>{errors.email}</span>}
                            
                            <input 
                                type="password" 
                                name="password" 
                                placeholder="Password" 
                                className="auth-input-field" 
                                onChange={handleRegisterChange}
                                value={registerData.password}
                                required
                            />
                            {errors.password && <span className="error-text" style={{ fontSize: '11px', color: 'red' }}>{errors.password}</span>}
                            
                            <input 
                                type="password" 
                                name="confirmPassword" 
                                placeholder="Confirm Password" 
                                className="auth-input-field" 
                                onChange={handleRegisterChange}
                                value={registerData.confirmPassword}
                                required
                            />
                            {errors.confirmPassword && <span className="error-text" style={{ fontSize: '11px', color: 'red' }}>{errors.confirmPassword}</span>}
                        </div>
                        {message && <p style={{ color: 'red', fontSize: '12px', margin: '5px 0' }}>{message}</p>}
                        <button className="auth-submit" style={{ width: '100%', marginTop: '10px' }} disabled={loading}>
                            {loading ? 'Creating...' : 'Sign Up'}
                        </button>
                        <div className="mobile-toggle-link" style={{ marginTop: '15px', fontSize: '14px' }}>
                            Already have an account? <span onClick={toggleMode} style={{ color: '#069668', cursor: 'pointer', fontWeight: 'bold' }}>Sign In</span>
                        </div>
                    </form>
                </div>

                {/* Sign In Form */}
                <div className="form-container sign-in-container">
                    <form className="auth-form-content" onSubmit={handleLoginSubmit}>
                        <img src="/Yathartha-Logo.png" alt="Yathartha" style={{ height: '40px', marginBottom: '10px' }} />
                        <h2>Welcome Back</h2>
                        {successMessage && <div className="success-message" style={{ width: '100%', padding: '8px' }}>{successMessage}</div>}
                        <div style={{ width: '100%' }}>
                            <input 
                                type="email" 
                                name="email" 
                                placeholder="Email" 
                                className="auth-input-field" 
                                onChange={handleLoginChange}
                                value={loginData.email}
                                required
                            />
                            <input 
                                type="password" 
                                name="password" 
                                placeholder="Password" 
                                className="auth-input-field" 
                                onChange={handleLoginChange}
                                value={loginData.password}
                                required
                            />
                        </div>
                        <Link to="/forgot-password" style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Forgot your password?</Link>
                        {message && <p style={{ color: 'red', fontSize: '12px', margin: '5px 0' }}>{message}</p>}
                        <button className="auth-submit" style={{ width: '100%', marginTop: '20px' }} disabled={loading}>
                            {loading ? 'Authenticating...' : 'Sign In'}
                        </button>
                        <div className="mobile-toggle-link" style={{ marginTop: '15px', fontSize: '14px' }}>
                            Don't have an account? <span onClick={toggleMode} style={{ color: '#069668', cursor: 'pointer', fontWeight: 'bold' }}>Sign Up</span>
                        </div>
                    </form>
                </div>

                {/* Overlay Panel */}
                <div className="overlay-container">
                    <div className="overlay">
                        <div className="overlay-panel overlay-left">
                            <h2 style={{ color: 'white' }}>Welcome Back!</h2>
                            <p style={{ color: 'white', opacity: 0.9 }}>To keep connected with us please login with your personal info</p>
                            <button className="ghost" onClick={toggleMode}>Sign In</button>
                        </div>
                        <div className="overlay-panel overlay-right">
                            <h2 style={{ color: 'white' }}>Hello, Friend!</h2>
                            <p style={{ color: 'white', opacity: 0.9 }}>Enter your personal details and start your financial journey with us</p>
                            <button className="ghost" onClick={toggleMode}>Sign Up</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;
